import type { AuthType } from '../types/auth';
import {
  classifyRefreshFailure,
  RefreshUnavailableError,
  SessionChangedError,
  SessionEndedError,
  type DeadSessionReason,
} from './refreshFailure';

/**
 * Puerta única del refresco del token (spec 2026-10-01 § 5.2).
 *
 * - Single-flight: N consultas que caducan a la vez → UN refresco. Sin esto,
 *   el primero rota el refresh token y los demás llegan con uno ya usado.
 * - Es el ÚNICO sitio de la app que puede cerrar la sesión, y solo cuando el
 *   propio refresco se rechaza con un código de sesión muerta. Si falla por
 *   red, plazo o 5xx, la sesión se conserva (Firebase, Supabase, Amplify).
 *
 * Las dependencias van inyectadas para poder probarlo sin montar Apollo.
 */
export interface RefreshSessionDeps {
  /** Llama a `RegenerateAccessToken` y devuelve el access token nuevo. */
  refresh: (refreshToken: string | undefined) => Promise<string | undefined>;
  getSession: () => AuthType | null;
  setSession: (session: AuthType) => void;
  /** Una vez por refresco rechazado de forma definitiva. */
  onSessionEnded: (reason: DeadSessionReason) => void;
  /** Una vez por refresco caído; el cliente lo reporta a Sentry. */
  onRefreshUnavailable?: (error: unknown) => void;
  /** Plazo del refresco; por defecto `REFRESH_DEADLINE_MS`. */
  deadlineMs?: number;
}

/**
 * 🔴 Plazo del refresco. Sin él, un refresco colgado (red degradada: el aparato
 * cree que hay red y la petición no vuelve) deja la puerta ocupada para
 * siempre: todas las 401 siguientes esperan a ese mismo refresco y la app no se
 * recupera sin recargar (escenario A6 de la batería). El mismo techo que las
 * consultas (`QUERY_DEADLINE_MS`).
 */
export const REFRESH_DEADLINE_MS = 12_000;

export class RefreshDeadlineError extends Error {
  constructor(ms: number) {
    super(`El refresco no respondió en ${ms} ms`);
    this.name = 'RefreshDeadlineError';
  }
}

/**
 * Un plazo NO cancela: la petición sigue y su respuesta llega tarde. Aquí se
 * ignora: el back no rota el refresh token al refrescar, así que perder ese
 * access token no estropea nada; el siguiente refresco pedirá otro.
 */
function withDeadline<T>(promise: Promise<T>, ms: number): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = setTimeout(() => reject(new RefreshDeadlineError(ms)), ms);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
}

export function createRefreshSingleFlight(
  deps: RefreshSessionDeps,
): () => Promise<string> {
  let inFlight: Promise<string> | null = null;

  const refreshOnce = async (): Promise<string> => {
    // 🔴 El refresco es de la sesión que lo PIDE. Si mientras está en vuelo
    // entra otra (logout + login de otro usuario en la misma tablet), su
    // resultado no puede tocarla: ni escribir en ella el token del anterior ni
    // cerrarla por un rechazo que no es suyo. Se reconoce por el refresh token.
    const usedRefreshToken = deps.getSession()?.refreshToken;
    const sessionChanged = () => deps.getSession()?.refreshToken !== usedRefreshToken;
    const sessionChangedError = () => new SessionChangedError();

    let accessToken: string | undefined;
    try {
      accessToken = await withDeadline(
        deps.refresh(usedRefreshToken),
        deps.deadlineMs ?? REFRESH_DEADLINE_MS,
      );
    } catch (error) {
      // Si ya hay otra sesión, el fallo no es suyo, sea cual sea: ni la cierra
      // ni se reporta como refresco caído.
      if (sessionChanged()) throw sessionChangedError();
      const failure = classifyRefreshFailure(error);
      if (failure.kind === 'session-ended') {
        deps.onSessionEnded(failure.reason);
        throw new SessionEndedError(failure.reason);
      }
      deps.onRefreshUnavailable?.(error);
      throw new RefreshUnavailableError(error);
    }
    if (!accessToken) {
      const tokenError = new Error('El refresco no devolvió token');
      deps.onRefreshUnavailable?.(tokenError);
      throw new RefreshUnavailableError(tokenError);
    }
    // Se relee: solo se escribe si sigue siendo la sesión que pidió el refresco.
    const current = deps.getSession();
    if (!current || current.refreshToken !== usedRefreshToken) throw sessionChangedError();
    deps.setSession({ ...current, accessToken });
    return accessToken;
  };

  return () => {
    if (!inFlight) {
      // Se libera SIEMPRE (éxito o error) para que la siguiente caducidad
      // pueda volver a refrescar.
      inFlight = refreshOnce().finally(() => {
        inFlight = null;
      });
    }
    return inFlight;
  };
}
